import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import StatCard from '../charts/StatCard';
import GaugeChart from '../charts/GaugeChart';
import { CalendarIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import api from '../../../services/api';

interface AppointmentIndicatorsProps {
  dateRange: { start_date?: string; end_date?: string };
}

export default function AppointmentIndicators({ dateRange }: AppointmentIndicatorsProps) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateRange.start_date) params.append('start_date', dateRange.start_date);
      if (dateRange.end_date) params.append('end_date', dateRange.end_date);

      const queryString = params.toString();
      const url = queryString ? `/dashboard/analytics/appointments/?${queryString}` : '/dashboard/analytics/appointments/';
      const response = await api.get(url);
      setData(response.data);
    } catch (error) {
      console.error('Error loading appointment indicators:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  if (!data) return null;

  const evolutionData = data.appointments_evolution?.map((item: any) => ({
    month: new Date(item.month).toLocaleDateString('fr-FR', { month: 'short' }),
    total: item.total,
    completed: item.completed,
    no_show: item.no_show,
    cancelled: item.cancelled
  })) || [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard title="Total RDV" value={data.total_appointments} icon={<CalendarIcon className="w-6 h-6" />} color="blue" />
        <StatCard title="Réalisés" value={data.completed} icon={<CheckCircleIcon className="w-6 h-6" />} color="green" />
        <StatCard title="Non honorés" value={data.non_honored} icon={<XCircleIcon className="w-6 h-6" />} color="red" />
        <StatCard title="Annulés" value={data.cancelled} icon={<XCircleIcon className="w-6 h-6" />} color="yellow" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution des Rendez-vous</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={evolutionData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="total" stroke="#3b82f6" strokeWidth={2} name="Total" />
              <Line type="monotone" dataKey="completed" stroke="#10b981" strokeWidth={2} name="Réalisés" />
              <Line type="monotone" dataKey="no_show" stroke="#ef4444" strokeWidth={2} name="Non honorés" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Taux d'Absentéisme</h3>
          <GaugeChart
            value={data.absenteeism_rate || 0}
            max={100}
            label="Pourcentage de RDV non honorés"
            color={data.absenteeism_rate < 10 ? 'green' : data.absenteeism_rate < 20 ? 'yellow' : 'red'}
          />
        </div>
      </div>
    </div>
  );
}
